/*
 ============================================================
  DISCOUNT CODES — storefront side

  The coupons table is admin-only (see backend/sql/coupons.sql), so
  there is deliberately no "list the codes" call here. The customer
  types one code and the database answers about that code alone.
 ============================================================
*/
import { supabase } from '../backend/lib/supabaseClient';
import { normaliseCode } from '../utils/coupon';

/* coupons.sql hasn't been run yet — the RPC doesn't exist. Same shape
   of check as the one in backend/utils/orderUtils.js. */
const isMissingFunction = (error) =>
  !!error &&
  (error.code === '42883' ||
    error.code === 'PGRST202' ||
    /could not find the function/i.test(error.message || '') ||
    /function .* does not exist/i.test(error.message || ''));

/**
 * Check one code against a subtotal.
 *
 * Always resolves to something renderable:
 *   { valid: true,  code, description, discount_type, discount_value,
 *     max_discount, min_order_amount, discount_amount }
 *   { valid: false, reason, message }
 *
 * reason 'below_minimum' also carries the rule fields: the code is
 * live, the basket is just too small yet, so the cart keeps it applied
 * and shows the shortfall.
 */
export const validateCoupon = async (code, subtotal) => {
  const { data, error } = await supabase.rpc('validate_coupon', {
    p_code: normaliseCode(code),
    p_subtotal: Number(subtotal) || 0,
  });

  if (error) {
    if (isMissingFunction(error)) {
      console.warn(
        'validate_coupon() not found — run backend/sql/coupons.sql to ' +
          'enable discount codes.'
      );
      return {
        valid: false,
        message: 'Discount codes aren’t available right now.',
      };
    }

    console.error('Coupon check failed:', error);
    return {
      valid: false,
      message: 'We couldn’t check that code. Please try again.',
    };
  }

  return data || { valid: false, message: 'That code isn’t valid.' };
};
